import { Component, Input, OnChanges, SimpleChanges, ViewContainerRef } from "@angular/core";
import { SearchClientService } from "@app2/clients/search-client.service";
import { CustomizeSearchColumnsModalComponent } from "@app2/search/search-shared/customize-search-columns-modal.component";
import { ModalService } from "@app2/shared/modal/modal.service";
import { NotificationsService } from "@app2/shared/services/notifications.service";
import { TimeService } from "@app2/shared/services/time.service";

@Component({
    selector: "expand-events",
    template: `
        <a class="link-text" (click)="toggleExpandedEvents()">
            <i class="far"
               [ngClass]="{'fa-chevron-up': expandedEvents, 'fa-chevron-down': !expandedEvents}"></i>
            {{(expandedEvents ? hideText : showText) | localize}}
        </a>
        <article *ngIf="expandedEvents">
            <div class="small-16 columns">
                <a class="pr-dropdown-button"
                   (click)="openCustomizeSearchColumnsModal()"
                   test-id="custom-columns-selector">
                    <i class="fa fa-columns"></i>
                    {{ "SEARCH_CUSTOMIZE_COLUMNS" | localize }}
                </a>
            </div>
            <events-table class="small-16 columns limit-results-height"
                          [queryString]="queryString"
                          [begin]="adjustedBegin"
                          [end]="adjustedEnd"
                          [customColumns]="customColumns"
                          (onEventsFetched)="onEventsFetched($event)">
            </events-table>
        </article>
    `,
    styles: [`
        .pr-dropdown-button {
            width: 118px;
            i {
                margin-right: 4px;
            }
        }
    `],
})
export class ExpandEventsComponent implements OnChanges {
    @Input() queryString: string;
    @Input() begin: Moment; //Optional
    @Input() end: Moment; //Optional

    @Input() showText: string;
    @Input() hideText: string;

    expandedEvents: boolean = false;
    NUM_EVENTS = 20;
    customColumns: string[] = [];
    adjustedBegin: Moment;
    adjustedEnd: Moment;
    // For the first query, we have `true` here, if that query fails to fetch enough events, we set this to `false`
    // and re-execute the query with a larger date range.
    beingOptimistic: boolean;

    constructor(private notificationsService: NotificationsService,
                private searchClient: SearchClientService,
                private timeService: TimeService,
                private modalService: ModalService,
                private viewContainerRef: ViewContainerRef) {
    }

    //This ngOnChanges bit is only needed because the trigger-details component is persistent through changing
    // triggers in the left alert list.
    ngOnChanges(simpleChanges: SimpleChanges): void {
        if (simpleChanges.queryString &&
            simpleChanges.queryString.currentValue !== simpleChanges.queryString.previousValue) {
            this.expandedEvents = false;
        }

        this.recalculateDates();
    }

    toggleExpandedEvents(): void {
        this.expandedEvents = !this.expandedEvents;

        if (this.expandedEvents) {
            this.beingOptimistic = true;
        }
    }

    recalculateDates(): void {
        if (this.begin && this.end) {
            this.adjustedBegin = this.begin;
            this.adjustedEnd = this.end;
        } else {
            this.adjustedEnd = this.timeService.now();
            this.adjustedBegin = this.adjustedEnd.clone().subtract(1, "days");
        }
    }

    /**
     * If too few events are fetched, increases the search range, but only once and only if the user didn't specify an
     * exact begin and end.
     */
    onEventsFetched(numEvents: number) {
        if (numEvents >= this.NUM_EVENTS || (this.begin && this.end)) {
            return;
        }

        if (this.beingOptimistic) {
            this.beingOptimistic = false;
            this.adjustedBegin = this.adjustedEnd.clone().subtract(30, "days");
        }
    }

    openCustomizeSearchColumnsModal(): void {
        this.modalService
            .open(CustomizeSearchColumnsModalComponent, {
                customColumns: this.customColumns,
            }, { viewContainerRef: this.viewContainerRef })
            .afterClosedWithResult()
            .subscribe(customColumns => this.customColumns = customColumns);
    }

}
